// Sound + system-notification helpers for new tickets and messages.
//
// Browsers refuse to play audio until the page has had a user gesture, and a
// support console usually sits untouched in a background tab for hours — a bare
// `new Audio().play()` is silently rejected there. So one AudioContext is created
// up front, unlocked on the first click/key/tap, and every chime goes through it.
// Once running, an AudioContext keeps playing while the tab is hidden.

const SOUND_URL = '/notification.mp3';
const SOUND_PREF_KEY = 'support-sound-enabled';

let ctx = null;
let buffer = null;
let loading = null;

function getCtx() {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  try { ctx = new AC(); } catch { ctx = null; }
  return ctx;
}

function loadBuffer() {
  if (buffer) return Promise.resolve(buffer);
  if (!loading) {
    loading = fetch(SOUND_URL)
      .then((r) => r.arrayBuffer())
      // Callback form — older Safari has no promise-returning decodeAudioData.
      .then((data) => new Promise((resolve, reject) => getCtx().decodeAudioData(data, resolve, reject)))
      .then((b) => { buffer = b; return b; })
      .catch((e) => { loading = null; throw e; });
  }
  return loading;
}

// resume() without a gesture can stay pending forever in Chrome, so never wait
// on it for longer than a moment.
function tryResume(c) {
  if (c.state === 'running') return Promise.resolve(true);
  return Promise.race([
    c.resume().then(() => c.state === 'running').catch(() => false),
    new Promise((r) => setTimeout(() => r(c.state === 'running'), 300)),
  ]);
}

export function isAudioReady() {
  return !!ctx && ctx.state === 'running';
}

// Creates the context, unlocks it on the first user gesture and reports every
// state change (Safari suspends it again when the app is backgrounded).
export function initAlertAudio(onChange) {
  const c = getCtx();
  if (!c) { onChange(false); return () => {}; }
  loadBuffer().catch(() => {});
  const report = () => onChange(c.state === 'running');
  const unlock = () => { tryResume(c).then(report); };
  const onVis = () => { if (document.visibilityState === 'visible') unlock(); };
  c.addEventListener?.('statechange', report);
  ['pointerdown', 'keydown', 'touchend'].forEach((ev) => window.addEventListener(ev, unlock, true));
  document.addEventListener('visibilitychange', onVis);
  tryResume(c).then(report);
  return () => {
    c.removeEventListener?.('statechange', report);
    ['pointerdown', 'keydown', 'touchend'].forEach((ev) => window.removeEventListener(ev, unlock, true));
    document.removeEventListener('visibilitychange', onVis);
  };
}

export function isSoundEnabled() {
  try { return localStorage.getItem(SOUND_PREF_KEY) !== 'off'; } catch { return true; }
}

export function setSoundEnabled(on) {
  try { localStorage.setItem(SOUND_PREF_KEY, on ? 'on' : 'off'); } catch {}
}

// Plays the chime `times` times back to back. Returns false when the browser
// still blocks audio, so the caller can ask the agent to click once.
export async function playAlertSound({ times = 1, force = false } = {}) {
  if (!force && !isSoundEnabled()) return false;
  try { navigator.vibrate?.([200, 100, 200]); } catch {}
  const c = getCtx();
  if (c && await tryResume(c)) {
    try {
      const b = await loadBuffer();
      let at = c.currentTime + 0.02;
      for (let i = 0; i < times; i++) {
        const src = c.createBufferSource();
        src.buffer = b;
        src.connect(c.destination);
        src.start(at);
        at += b.duration + 0.18;
      }
      return true;
    } catch {}
  }
  try { await new Audio(SOUND_URL).play(); return true; } catch { return false; }
}

// OS-level notification. Goes through the service worker registration because
// the `new Notification()` constructor throws on Android and iOS. The tag
// matches the one the push function uses (`case-<id>`), so when a push for the
// same case also lands the two collapse into one.
export async function showSystemNotification(title, { body = '', caseId = '' } = {}) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const options = {
    body,
    icon: '/logo192.png',
    badge: '/logo192.png',
    tag: caseId ? `case-${caseId}` : `support-${Date.now()}`,
    renotify: true,
    requireInteraction: true,
    data: { caseId },
  };
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg) { await reg.showNotification(title, options); return; }
  } catch {}
  try { new Notification(title, options); } catch {}
}
